import type { LocationCoordinate } from '@/store/location';
import type { GeocodeResult } from '@/types';
import { supabase } from '@/lib/supabase';

declare global {
  interface Window {
    naver: any;
  }
}

interface NaverMapOptions {
  center: LocationCoordinate;
  zoom: number;
  zoomControl?: boolean;
  zoomControlOptions?: {
    position: any;
  };
}

class NaverMapService {
  private mapLoaded: boolean = false;
  private loadPromise: Promise<void> | null = null;
  private readonly clientId: string;

  constructor() {
    // 기존 CLIENT_ID와 새로운 KEY_ID 모두 지원
    this.clientId = import.meta.env.VITE_NAVER_KEY_ID || import.meta.env.VITE_NAVER_CLIENT_ID || '';
    if (!this.clientId) {
      console.error('네이버 지도 API 키가 설정되지 않았습니다. VITE_NAVER_KEY_ID를 설정해주세요.');
    }
  }

  /**
   * 네이버 지도 SDK 동적 로드
   */
  async loadMapScript(): Promise<void> {
    // 이미 로드된 경우 즉시 반환
    if (this.mapLoaded && window.naver && window.naver.maps) {
      return;
    }
    
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      // HTML에서 이미 스크립트가 로드되어 있는지 확인
      if (window.naver && window.naver.maps) {
        this.mapLoaded = true;
        resolve();
        return;
      }

      // 인증 실패 콜백 함수 등록
      (window as any).navermap_authFailure = () => {
        console.error('네이버 지도 API 인증 실패');
        this.mapLoaded = false;
        this.loadPromise = null;
        reject(new Error('네이버 지도 API 인증 실패'));
      };

      const script = document.createElement('script');
      script.type = 'text/javascript';
      // 새로운 네이버 Maps API 형식 (ncpKeyId 사용)
      script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${this.clientId}&submodules=geocoder`;
      script.async = true;
      
      script.onload = () => {
        // 네이버 지도가 완전히 로드될 때까지 대기
        const checkLoaded = setInterval(() => {
          if (window.naver && window.naver.maps) {
            clearInterval(checkLoaded);
            this.mapLoaded = true;
            resolve();
          }
        }, 100);
        
        // 2초 후에도 로드되지 않으면 실패
        setTimeout(() => {
          clearInterval(checkLoaded);
          if (!this.mapLoaded) {
            reject(new Error('네이버 지도 SDK 로드 타임아웃'));
          }
        }, 2000);
      };
      
      script.onerror = () => {
        this.mapLoaded = false;
        this.loadPromise = null;
        reject(new Error('네이버 지도 SDK 로드 실패'));
      };
      
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * 지도 생성
   */
  async createMap(containerId: string, options: NaverMapOptions): Promise<any> {
    await this.loadMapScript();
    
    if (!window.naver || !window.naver.maps) {
      throw new Error('네이버 지도 API가 로드되지 않았습니다.');
    }

    const mapOptions = {
      center: new window.naver.maps.LatLng(options.center.latitude, options.center.longitude),
      zoom: options.zoom || 16,
      minZoom: 8,
      maxZoom: 19,
      zoomControl: options.zoomControl ?? true,
      zoomControlOptions: options.zoomControlOptions || {
        position: window.naver.maps.Position.TOP_RIGHT
      }
    };

    return new window.naver.maps.Map(containerId, mapOptions);
  }

  /**
   * 마커 생성
   */
  createMarker(map: any, position: LocationCoordinate, options?: any): any {
    if (!window.naver || !window.naver.maps) {
      throw new Error('네이버 지도 API가 로드되지 않았습니다.');
    }

    return new window.naver.maps.Marker({
      position: new window.naver.maps.LatLng(position.latitude, position.longitude),
      map: map,
      ...options
    });
  }

  /**
   * 지도 중심 좌표 가져오기
   */
  getMapCenter(map: any): LocationCoordinate {
    const center = map.getCenter();
    return {
      latitude: center.lat(),
      longitude: center.lng()
    };
  }

  /**
   * 지도 중심 이동
   */
  setMapCenter(map: any, position: LocationCoordinate, smooth: boolean = true): void {
    const latlng = new window.naver.maps.LatLng(position.latitude, position.longitude);
    
    if (smooth) {
      map.panTo(latlng);
    } else {
      map.setCenter(latlng);
    }
  }

  /**
   * 좌표를 주소로 변환 (Reverse Geocoding)
   * 주의: 클라이언트에서 직접 호출 시 CORS 문제 발생
   * 실제 구현은 서버사이드 또는 Edge Function 필요
   */
  async reverseGeocode(coordinate: LocationCoordinate): Promise<{ roadAddress: string; jibunAddress: string }> {
    // 임시 구현 - 실제로는 서버사이드 API 호출 필요
    console.log('Reverse geocoding for:', coordinate);
    
    // 더미 데이터 반환
    return {
      roadAddress: `위도 ${coordinate.latitude.toFixed(6)}, 경도 ${coordinate.longitude.toFixed(6)} 지점`,
      jibunAddress: `위도 ${coordinate.latitude.toFixed(6)}, 경도 ${coordinate.longitude.toFixed(6)} 지점`
    };
  }

  /**
   * 지도 API 로드 여부 확인
   */
  isMapLoaded(): boolean {
    return this.mapLoaded && window.naver && window.naver.maps;
  }

  /**
   * 주소를 좌표로 변환 (Geocoding)
   * Supabase Edge Function을 통해 네이버 API 호출
   */
  async geocode(address: string): Promise<GeocodeResult> {
    try {
      console.log(`🔍 지오코딩 시작: "${address}"`);
      
      const { data, error } = await supabase.functions.invoke('geocode', {
        body: { address }
      });

      if (error) {
        console.error('❌ 지오코딩 Edge Function 호출 오류:', error);
        return {
          success: false,
          error: 'FUNCTION_ERROR',
          message: error.message
        };
      }

      // Edge Function이 반환한 결과 처리
      if (!data.success) {
        // 실패 케이스별 상세 로그
        switch (data.error) {
          case 'API_KEY_MISSING':
            console.error('❌ 네이버 API 키가 설정되지 않았습니다.');
            break;
          case 'AUTH_FAILED':
            console.error('❌ 네이버 API 인증 실패 (401). API 키를 확인해주세요.');
            break;
          case 'SERVICE_NOT_SUBSCRIBED':
            console.error('❌ 네이버 Maps API 서비스 미구독 (210). 서비스 구독을 확인해주세요.');
            break;
          case 'NO_RESULT':
            console.warn(`⚠️ 주소 "${address}"에 대한 검색 결과가 없습니다.`);
            break;
          default:
            console.error(`❌ 지오코딩 실패: ${data.message}`);
        }
        return data;
      }

      // 성공
      return data;
    } catch (error) {
      console.error('❌ 지오코딩 API 호출 중 예외 발생:', error);
      return {
        success: false,
        error: 'EXCEPTION',
        message: error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'
      };
    }
  }
}

export const naverMapService = new NaverMapService();